import {
  OrderPage,
  BasketPage,
  CatalogPage,
  LandingPage,
  ProductPage,
  SuccessPage,
  ProfilePage,
  SettingsPage,
  DeveloperPage,
  DocumentsPage,
  OrdersHistoryPage,
  AboutRestaurantPage,
  EmptyDeliveryTypesPage,
  DocumentViewPage,
  OAuthCallbackPage,
  MagicLinkVerifyPage,
} from './pages';
import React from 'react';
import { PATH } from './path';
import { BaseLayout } from './layouts';
import { ChainProvider } from './guards';
import { getFaroInstance } from '../config/logging';
import { RouteWithMeta, RouteChangeCallback } from './types';
import { createBrowserRouter, Location } from 'react-router-dom';
import { withFaroRouterInstrumentation } from '@grafana/faro-react';
import { PageOrModalRoutes } from './pageOrModalRoutes';
import { readAndClearMagicLinkEntry } from '@/features/auth';

const routes: RouteWithMeta[] = [
  {
    path: '*',
    element: React.createElement(LandingPage),
    meta: { name: 'Not Found', section: 'error' },
  },
  {
    path: PATH.landing,
    element: React.createElement(LandingPage),
    meta: { name: 'Landing', section: 'main' },
  },
  {
    path: PATH.developer,
    element: React.createElement(DeveloperPage),
    meta: { name: 'Developer', section: 'dev' },
  },
  {
    path: PATH.oauthCallback,
    element: React.createElement(OAuthCallbackPage),
    meta: { name: 'OAuth Callback', section: 'auth' },
  },
  {
    element: React.createElement(ChainProvider, null, React.createElement(BaseLayout)),
    meta: { name: 'App Layout', section: 'app' },
    children: [
      {
        path: PATH.magicLinkVerify,
        loader: readAndClearMagicLinkEntry,
        element: React.createElement(MagicLinkVerifyPage),
        meta: { name: 'Magic Link Verification', section: 'auth' },
      },
      {
        path: PATH.order,
        element: React.createElement(OrderPage),
        meta: { name: 'Order Details', section: 'orders' },
      },
      {
        path: '/*',
        element: React.createElement(PageOrModalRoutes),
        meta: { name: 'Pages With Modal Overlay', section: 'shopping' },
        children: [
          {
            path: PATH.catalog.slice(1),
            element: React.createElement(CatalogPage),
            meta: { name: 'Catalog', section: 'shopping' },
          },
          {
            path: PATH.basket.slice(1),
            element: React.createElement(BasketPage),
            meta: { name: 'Shopping Cart', section: 'orders' },
          },
          {
            path: PATH.product.slice(1),
            element: React.createElement(ProductPage),
            meta: { name: 'Product Details', section: 'shopping' },
          },
        ],
      },
      {
        path: PATH.success,
        element: React.createElement(SuccessPage),
        meta: { name: 'Order Success', section: 'orders' },
      },
      {
        path: PATH.profile,
        element: React.createElement(ProfilePage),
        meta: { name: 'User Profile', section: 'account' },
      },
      {
        path: PATH.settings,
        element: React.createElement(SettingsPage),
        meta: { name: 'Settings', section: 'account' },
      },
      {
        path: PATH.ordersHistory,
        element: React.createElement(OrdersHistoryPage),
        meta: { name: 'Order History', section: 'orders' },
      },
      {
        path: PATH.aboutPage,
        element: React.createElement(AboutRestaurantPage),
        meta: { name: 'About Restaurant', section: 'account' },
      },
      {
        path: PATH.documents,
        element: React.createElement(DocumentsPage),
        meta: { name: 'Documents', section: 'account' },
      },
      {
        path: PATH.documentView,
        element: React.createElement(DocumentViewPage),
        meta: { name: 'Document View', section: 'account' },
      },
      {
        path: PATH.emptyDeliveryTypes,
        element: React.createElement(EmptyDeliveryTypesPage),
        meta: { name: 'No Delivery Options', section: 'error' },
      },
    ],
  },
] as const;

// Get route metadata for logging
const flattenRoutes = (route: RouteWithMeta): RouteWithMeta[] =>
  route.children ? [route, ...route.children.flatMap(flattenRoutes)] : [route];

const getRouteMetadata = (pathname: string) => {
  const matchingRoute = routes.flatMap(flattenRoutes).find((route) => {
    if (!route.path) return false;
    // Convert route path pattern to regex
    const pattern = route.path.replace(/:[^/]+/g, '[^/]+');
    return new RegExp(`^${pattern}$`).test(pathname);
  });

  return matchingRoute?.meta || { name: pathname, section: 'unknown' };
};

// Create instrumented router
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const reactBrowserRouter = createBrowserRouter(routes as any[]);

// Create route change callback
export const routeChangeCallback: RouteChangeCallback = (location: Location) => {
  const metadata = getRouteMetadata(location.pathname);
  return {
    name: metadata.name,
    properties: {
      path: location.pathname,
      section: metadata.section,
      search: location.pathname === PATH.magicLinkVerify ? '' : location.search,
      hash: location.hash,
    },
  };
};

// Initialize Faro with router instrumentation
const faro = getFaroInstance();
faro.api.pushLog([
  {
    level: 'info',
    message: 'Router instrumentation initialized',
    context: {
      routes: routes.map((r) => ({ path: r.path, name: r.meta.name })),
    },
  },
]);

export const router = withFaroRouterInstrumentation(reactBrowserRouter);
